import React, { useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { CiSearch } from "react-icons/ci";
import AddIcon from "@mui/icons-material/Add";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditSharpIcon from "@mui/icons-material/EditSharp";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import PersonOffRoundedIcon from "@mui/icons-material/PersonOffRounded";
import FamilyRestroomRoundedIcon from "@mui/icons-material/FamilyRestroomRounded";
import FilterAltOffRoundedIcon from "@mui/icons-material/FilterAltOffRounded";
import { useNavigate } from "react-router-dom";
import { StepUpSpinner } from "../../../Components/Loader";
import { useNotifier } from "../../../Core/Notifier";
import { useConfirm, confirmPresets } from "../../../Components/Confirm";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import DeletePopup from "../../../Components/Delete/DeletePopup";
import Pagination from "../../../Components/Pagination";
import useUser from "../../../Core/hook/useUser";
import {
  useGetAllParentsQuery,
  useDeleteParentMutation,
  useToggleParentAccessMutation,
} from "../../../Api/attoDeskApi";

interface IParentChild {
  student_id: number;
  name: string;
  grade?: string;
  location?: string;
  relationship_type?: string;
  isPrimary?: boolean;
  access_status?: string;
}
interface IParentRow {
  id: string;
  name: string;
  email: string;
  phone: string;
  username: string | null;
  has_account: boolean;
  status: "active" | "disabled" | "no_account";
  children: IParentChild[];
  children_count: number;
}

type LinkFilter = "all" | "with" | "without";
type StatusFilter = "all" | "active" | "disabled" | "no_account";

const AVATAR_PALETTE = [
  { bg: "#dbeafe", fg: "#1e40af" }, // soft blue
  { bg: "#ede9fe", fg: "#5b21b6" }, // soft lavender
  { bg: "#d1fae5", fg: "#065f46" }, // soft mint
  { bg: "#ffedd5", fg: "#9a3412" }, // soft peach
  { bg: "#ccfbf1", fg: "#0f766e" }, // soft teal
  { bg: "#fce7f3", fg: "#9d174d" }, // soft pink
  { bg: "#fef3c7", fg: "#92400e" }, // soft amber
  { bg: "#e0e7ff", fg: "#3730a3" }, // soft indigo
];

const initialsOf = (name: string) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  const first = parts[0][0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] || "" : "";
  return (first + last).toUpperCase() || "?";
};

const swatchFor = (key: string) => {
  let sum = 0;
  for (let i = 0; i < key.length; i++) sum = (sum + key.charCodeAt(i)) % 997;
  return AVATAR_PALETTE[sum % AVATAR_PALETTE.length];
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const map: Record<string, { label: string; bg: string; color: string; dot: string; border: string }> = {
    active:    { label: "Active",   bg: "#dcfce7", color: "#166534", dot: "#16a34a", border: "#86efac" },
    disabled:  { label: "Disabled", bg: "#fee2e2", color: "#991b1b", dot: "#dc2626", border: "#fecaca" },
    no_account:{ label: "No Login", bg: "#ede9fe", color: "#5b21b6", dot: "#8b5cf6", border: "#ddd6fe" },
  };
  const c = map[status] || map.no_account;
  return (
    <Chip
      size="small"
      label={
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: c.dot }} />
          <span>{c.label}</span>
        </Stack>
      }
      sx={{
        bgcolor: c.bg, color: c.color, fontWeight: 700, fontSize: 11.5,
        border: `1px solid ${c.border}`, "& .MuiChip-label": { px: 1.25 },
      }}
    />
  );
};

const SummaryCard: React.FC<{
  label: string;
  value: number;
  icon: React.ReactNode;
  tint: string;
  fg: string;
  sub?: string;
  borderColor?: string;
  tileBg?: string;
  metricColor?: string;
  shadowColor?: string;
  selected?: boolean;
  onClick?: () => void;
  ariaLabel?: string;
}> = ({
  label, value, icon, tint, fg, sub,
  borderColor, tileBg, metricColor, shadowColor,
  selected, onClick, ariaLabel,
}) => (
  <Card
    elevation={0}
    role="button"
    tabIndex={0}
    aria-pressed={!!selected}
    aria-label={ariaLabel || `Filter parents: ${label}`}
    onClick={onClick}
    onKeyDown={(e: React.KeyboardEvent) => {
      if (onClick && (e.key === "Enter" || e.key === " ")) {
        e.preventDefault();
        onClick();
      }
    }}
    sx={{
      p: 2.25,
      borderRadius: 2.5,
      cursor: onClick ? "pointer" : "default",
      position: "relative",
      border: `${selected ? 2 : 1}px solid ${
        selected ? (metricColor || fg) : borderColor || "#e2e8f0"
      }`,
      background: `linear-gradient(135deg, ${tint} 0%, #ffffff 100%)`,
      boxShadow: selected
        ? `0 0 0 3px ${shadowColor || "rgba(15,23,42,.08)"}, 0 6px 18px ${shadowColor || "rgba(15,23,42,.06)"}`
        : shadowColor ? `0 2px 10px ${shadowColor}` : "0 1px 2px rgba(15,23,42,.03)",
      transition: "transform .18s ease, box-shadow .18s ease, border-color .18s ease",
      "&:hover": onClick
        ? {
            transform: "translateY(-2px)",
            boxShadow: selected
              ? `0 0 0 3px ${shadowColor || "rgba(15,23,42,.08)"}, 0 10px 24px ${shadowColor || "rgba(15,23,42,.08)"}`
              : shadowColor ? `0 8px 22px ${shadowColor}` : "0 6px 20px rgba(15,23,42,.06)",
          }
        : undefined,
      "&:focus-visible": {
        outline: "none",
        boxShadow: `0 0 0 3px ${shadowColor || "rgba(59,130,246,.25)"}, 0 6px 18px rgba(15,23,42,.06)`,
      },
      height: "100%",
    }}
  >
    {selected && (
      <Box
        aria-hidden
        sx={{
          position: "absolute", top: 8, right: 8,
          width: 20, height: 20, borderRadius: "50%",
          bgcolor: metricColor || fg, color: "#ffffff",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: `0 2px 6px ${shadowColor || "rgba(15,23,42,.15)"}`,
        }}
      >
        <CheckRoundedIcon sx={{ fontSize: 14 }} />
      </Box>
    )}
    <Stack direction="row" spacing={1.75} alignItems="center">
      <Box
        sx={{
          width: 44, height: 44, borderRadius: 2,
          bgcolor: tileBg || "#ffffff", color: fg,
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 1px 3px rgba(15,23,42,.06)",
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: .8, color: "#64748b", textTransform: "uppercase" }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 24, fontWeight: 800, color: metricColor || "#0f172a", lineHeight: 1.1 }}>
          {value}
        </Typography>
        {sub && (
          <Typography sx={{ fontSize: 11.5, color: "#64748b", mt: 0.25 }}>{sub}</Typography>
        )}
      </Box>
    </Stack>
  </Card>
);

const Muted: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ color: "#94a3b8", fontStyle: "italic", fontSize: 13 }}>
    {children ?? "Not provided"}
  </Typography>
);

const ParentListPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { showMessage, showErrorMessage } = useNotifier();
  const confirm = useConfirm();
  const { canCreate, canEdit, canDelete } = useUser() as any;

  const [searchQuery, setSearchQuery] = useState("");
  const debounced = useDebounce(searchQuery, 300);
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [linkFilter, setLinkFilter] = useState<LinkFilter>("all");

  const { data, isLoading, isError } = useGetAllParentsQuery({ searchText: debounced });
  const [deleteParent] = useDeleteParentMutation();
  const [toggleAccess] = useToggleParentAccessMutation();

  const [toDelete, setToDelete] = useState<IParentRow | null>(null);
  const [openDelete, setOpenDelete] = useState(false);

  type ResolveMode = "disable" | "detach" | "delete";
  const [toResolve, setToResolve] = useState<IParentRow | null>(null);
  const [resolveMode, setResolveMode] = useState<ResolveMode>("disable");

  const parents: IParentRow[] = useMemo(
    () => (Array.isArray(data?.data) ? (data!.data as IParentRow[]) : []),
    [data]
  );

  const summary = useMemo(() => {
    const total = parents.length;
    let active = 0, noLogin = 0, linkedChildren = 0;
    parents.forEach((p) => {
      if (p.status === "active") active += 1;
      if (!p.has_account) noLogin += 1;
      linkedChildren += p.children_count || 0;
    });
    return { total, active, noLogin, linkedChildren };
  }, [parents]);

  const filtered = useMemo(() => {
    return parents.filter((p) => {
      if (statusFilter !== "all") {
        if (statusFilter === "no_account" && p.has_account) return false;
        if (statusFilter === "active" && p.status !== "active") return false;
        if (statusFilter === "disabled" && p.status !== "disabled") return false;
      }
      if (linkFilter === "with" && p.children_count <= 0) return false;
      if (linkFilter === "without" && p.children_count > 0) return false;
      return true;
    });
  }, [parents, statusFilter, linkFilter]);

  const paged = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page]
  );

  const resetFilters = () => {
    setStatusFilter("all");
    setLinkFilter("all");
    setSearchQuery("");
    setPage(0);
  };
  const hasActiveFilters = statusFilter !== "all" || linkFilter !== "all" || !!searchQuery;

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      const res = await deleteParent({ id: toDelete.id }).unwrap();
      res.status ? showMessage(res.message) : showErrorMessage(res.message);
    } catch {
      showErrorMessage("Failed to delete parent");
    } finally {
      setOpenDelete(false);
      setToDelete(null);
    }
  };

  const requestDelete = (row: IParentRow) => {
    if (row.children_count > 0) {
      setResolveMode(row.has_account ? "disable" : "detach");
      setToResolve(row);
    } else {
      setToDelete(row);
      setOpenDelete(true);
    }
  };

  const closeResolve = () => {
    setToResolve(null);
    setResolveMode("disable");
  };

  const handleResolve = async () => {
    if (!toResolve) return;
    try {
      const res =
        resolveMode === "disable"
          ? await toggleAccess({ parent_id: toResolve.id, status: "disabled" }).unwrap()
          : await deleteParent({ id: toResolve.id, mode: resolveMode }).unwrap();
      res.status ? showMessage(res.message) : showErrorMessage(res.message);
    } catch {
      showErrorMessage("Failed to update parent");
    } finally {
      closeResolve();
    }
  };

  const handleToggle = async (row: IParentRow) => {
    if (!row.has_account) {
      showErrorMessage("This parent has no portal account to enable/disable.");
      return;
    }
    const next = row.status === "active" ? "disabled" : "active";
    const run = async () => {
      const res = await toggleAccess({ parent_id: row.id, status: next }).unwrap();
      if (!res.status) throw new Error(res.message);
    };
    if (next === "disabled") {
      await confirm({
        ...confirmPresets.disableAccount(),
        message: `Are you sure you want to disable portal access for "${row.name}"?\nThey will no longer be able to log in.`,
        successMessage: "Parent access disabled.",
        onConfirm: run,
      });
      return;
    }
    try {
      await run();
      showMessage("Parent access enabled.");
    } catch (e: any) {
      showErrorMessage(e?.message || "Failed to update parent access");
    }
  };

  const renderChildren = (children: IParentChild[]) => {
    if (!children.length) {
      return (
        <Chip
          size="small"
          label="No linked children"
          sx={{
            bgcolor: "#f5f3ff",
            color: "#7c3aed",
            border: "1px solid #ede9fe",
            fontWeight: 600,
            fontSize: 11,
          }}
        />
      );
    }
    const chipPalette = [
      { bg: "#eff6ff", border: "#bfdbfe", grade: "#3b82f6" }, // soft blue
      { bg: "#f5f3ff", border: "#ddd6fe", grade: "#8b5cf6" }, // soft lavender
    ];
    const shown = children.slice(0, 2);
    return (
      <Stack direction="row" spacing={0.75} flexWrap="wrap" rowGap={0.75}>
        {shown.map((c, idx) => {
          const dim = c.access_status === "disabled";
          const pal = chipPalette[idx % chipPalette.length];
          return (
            <Chip
              key={c.student_id}
              size="small"
              label={
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: dim ? "#94a3b8" : "#0f172a" }}>
                    {c.name}
                  </Typography>
                  {c.grade && (
                    <Typography sx={{ fontSize: 10.5, color: dim ? "#cbd5e1" : pal.grade, fontWeight: 600 }}>
                      · {c.grade}
                    </Typography>
                  )}
                </Stack>
              }
              sx={{
                bgcolor: pal.bg,
                border: `1px solid ${pal.border}`,
                textDecoration: dim ? "line-through" : "none",
                opacity: dim ? 0.65 : 1,
                transition: "border-color .15s ease, box-shadow .15s ease",
                "&:hover": { borderColor: pal.grade, boxShadow: `0 2px 6px ${pal.bg}` },
                "& .MuiChip-label": { px: 1 },
              }}
            />
          );
        })}
        {children.length > 2 && (
          <Chip
            size="small"
            label={`+${children.length - 2} more`}
            sx={{
              bgcolor: "#e0e7ff",
              color: "#4338ca",
              border: "1px solid #c7d2fe",
              fontWeight: 700,
              fontSize: 11,
            }}
          />
        )}
      </Stack>
    );
  };

  const renderActions = (row: IParentRow) => {
    const iconBtn = (bg: string, hover: string, fg: string, border: string, children: React.ReactNode, onClick: () => void, title: string, key: string) => (
      <Tooltip title={title} key={key}>
        <IconButton
          size="small"
          onClick={onClick}
          sx={{
            width: 32, height: 32, borderRadius: 1.5,
            bgcolor: bg, color: fg,
            border: `1px solid ${border}`,
            transition: "transform .12s ease, box-shadow .12s ease, background-color .12s ease",
            "&:hover": { bgcolor: hover, transform: "translateY(-1px)", boxShadow: `0 3px 8px ${border}` },
          }}
        >
          {children}
        </IconButton>
      </Tooltip>
    );
    const btns: React.ReactNode[] = [];
    btns.push(iconBtn("#eff6ff", "#dbeafe", "#1d4ed8", "#bfdbfe",
      <VisibilityOutlinedIcon fontSize="small" />,
      () => navigate(`/parents/view/${row.id}`), "View details", "v"));
    if (canEdit("Parent")) {
      btns.push(iconBtn("#fffbeb", "#fef3c7", "#b45309", "#fde68a",
        <EditSharpIcon fontSize="small" />,
        () => navigate(`/parents/edit/${row.id}`), "Edit", "e"));
    }
    if (canEdit("Parent") && row.has_account) {
      const isActive = row.status === "active";
      btns.push(iconBtn(
        isActive ? "#fff7ed" : "#ecfdf5",
        isActive ? "#ffedd5" : "#d1fae5",
        isActive ? "#c2410c" : "#16a34a",
        isActive ? "#fed7aa" : "#a7f3d0",
        isActive ? <BlockIcon fontSize="small" /> : <CheckCircleOutlineIcon fontSize="small" />,
        () => handleToggle(row),
        isActive ? "Disable access" : "Enable access",
        "t"));
    }
    if (canDelete("Parent")) {
      btns.push(iconBtn("#fef2f2", "#fee2e2", "#dc2626", "#fecaca",
        <DeleteOutlineSharpIcon fontSize="small" />,
        () => requestDelete(row), "Delete", "d"));
    }
    return <Stack direction="row" spacing={0.75} justifyContent={{ xs: "flex-start", md: "flex-end" }}>{btns}</Stack>;
  };

  const ParentAvatar: React.FC<{ row: IParentRow }> = ({ row }) => {
    const c = swatchFor(row.id || row.name || "?");
    return (
      <Avatar
        sx={{
          width: 40, height: 40,
          bgcolor: c.bg, color: c.fg,
          fontWeight: 800, fontSize: 14,
          border: `1px solid ${c.fg}22`,
          transition: "box-shadow .15s ease, transform .15s ease",
        }}
      >
        {initialsOf(row.name || row.email || "?")}
      </Avatar>
    );
  };

  const rowAccent = (status: string) => {
    if (status === "active") return { border: "#22c55e", hover: "#f0fdf4" };
    if (status === "disabled") return { border: "#f97316", hover: "#fff7ed" };
    return { border: "#a78bfa", hover: "#faf5ff" };
  };

  // ── Mobile card row ────────────────────────────────────────────
  const MobileCard: React.FC<{ row: IParentRow }> = ({ row }) => {
    const accent = rowAccent(row.status);
    return (
    <Card
      elevation={0}
      sx={{
        p: 2, mb: 1.5, borderRadius: 2.5,
        border: "1px solid #e2e8f0",
        borderLeft: `3px solid ${accent.border}`,
        bgcolor: "#ffffff",
        transition: "box-shadow .15s ease, transform .15s ease",
        "&:hover": { boxShadow: "0 6px 18px rgba(15,23,42,.08)", transform: "translateY(-1px)" },
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
        <ParentAvatar row={row} />
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 15, color: "#0f172a" }} noWrap>
            {row.name || "—"}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: "#7c8aa3" }}>Parent Account</Typography>
        </Box>
        <StatusBadge status={row.status} />
      </Stack>
      <Divider sx={{ mb: 1.5 }} />
      <Stack spacing={0.75} mb={1.5}>
        <Stack direction="row" spacing={1}>
          <Typography sx={{ fontSize: 11.5, color: "#64748b", width: 78, fontWeight: 700, textTransform: "uppercase", letterSpacing: .4 }}>Email</Typography>
          {row.email ? <Typography sx={{ fontSize: 13, color: "#0f172a", wordBreak: "break-all" }}>{row.email}</Typography> : <Muted />}
        </Stack>
        <Stack direction="row" spacing={1}>
          <Typography sx={{ fontSize: 11.5, color: "#64748b", width: 78, fontWeight: 700, textTransform: "uppercase", letterSpacing: .4 }}>Phone</Typography>
          {row.phone ? <Typography sx={{ fontSize: 13, color: "#0f172a" }}>{row.phone}</Typography> : <Muted />}
        </Stack>
        <Stack direction="row" spacing={1}>
          <Typography sx={{ fontSize: 11.5, color: "#64748b", width: 78, fontWeight: 700, textTransform: "uppercase", letterSpacing: .4 }}>Username</Typography>
          {row.username ? <Typography sx={{ fontSize: 13, color: "#0f172a", fontFamily: "monospace" }}>{row.username}</Typography> : <Muted />}
        </Stack>
        <Box>
          <Typography sx={{ fontSize: 11.5, color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: .4, mb: 0.75 }}>Linked Children</Typography>
          {renderChildren(row.children)}
        </Box>
      </Stack>
      {renderActions(row)}
    </Card>
  );
  };

  return (
    <Box
      sx={{
        px: { xs: 1.5, md: 2 },
        py: 2,
        minHeight: "100%",
        background:
          "linear-gradient(180deg, #f4f7fb 0%, #f7f9fc 40%, #f5f8fb 100%)",
      }}
    >
      {/* ── Hero header card ───────────────────────────────────── */}
      <Card
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          mb: 2.5,
          borderRadius: 3,
          border: "1px solid #dbe6f3",
          background:
            "linear-gradient(120deg, #e0efff 0%, #eaf3ff 40%, #f4faf6 100%)",
          boxShadow: "0 4px 18px rgba(37,99,235,.06)",
        }}
      >
        <Grid container spacing={2} alignItems="center" justifyContent="space-between">
          <Grid item xs={12} md="auto">
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 52, height: 52, borderRadius: 2.5,
                  bgcolor: "#e0e7ff",
                  color: "#1e3a8a",
                  border: "1px solid #c7d2fe",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  boxShadow: "0 3px 8px rgba(30,58,138,.15)",
                }}
              >
                <FamilyRestroomRoundedIcon />
              </Box>
              <Box>
                <Typography sx={{ fontSize: { xs: 20, md: 24 }, fontWeight: 800, color: "#0f172a", lineHeight: 1.15 }}>
                  Parents
                </Typography>
                <Typography sx={{ fontSize: 13.5, color: "#475569", mt: 0.25, maxWidth: 520 }}>
                  Manage parent accounts, linked children, login access, and contact details.
                </Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} md="auto">
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} alignItems={{ sm: "center" }}>
              <TextField
                size="small"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
                placeholder="Search parents…"
                sx={{
                  minWidth: { sm: 240 },
                  "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#ffffff" },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CiSearch />
                    </InputAdornment>
                  ),
                }}
              />
              {canCreate("Parent") && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate("/parents/create")}
                  sx={{
                    bgcolor: "#166534",
                    fontWeight: 700,
                    borderRadius: 2,
                    px: 2.25, py: 1,
                    textTransform: "none",
                    boxShadow: "0 2px 8px rgba(22,101,52,.25)",
                    "&:hover": { bgcolor: "#14532d" },
                  }}
                >
                  Add Parent
                </Button>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Card>

      {/* ── Summary stat cards ────────────────────────────────── */}
      {(() => {
        // `Total Parents` is a show-all / reset action — never a
        // persistent selected state, even when the other filters
        // happen to be at their defaults on initial page load.
        const totalSelected = false;
        const activeSelected = statusFilter === "active";
        const noLoginSelected = statusFilter === "no_account";
        const linkedSelected = linkFilter === "with";
        const selectTotal = () => {
          resetFilters();
        };
        const toggleStatus = (next: StatusFilter) => {
          setStatusFilter((prev) => (prev === next ? "all" : next));
          setPage(0);
        };
        const toggleLinked = () => {
          setLinkFilter((prev) => (prev === "with" ? "all" : "with"));
          setPage(0);
        };
        return (
          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            <Grid item xs={6} md={3}>
              <SummaryCard
                label="Total Parents" value={summary.total}
                tint="#eaf2ff" tileBg="#dbe4ff" fg="#1e3a8a"
                borderColor="#c7d7f5" metricColor="#1d4ed8"
                shadowColor="rgba(37,99,235,.15)"
                icon={<PeopleAltRoundedIcon />} sub="All registered parents"
                selected={totalSelected}
                onClick={selectTotal}
                ariaLabel="Show All Parents"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <SummaryCard
                label="Active Login" value={summary.active}
                tint="#e8f8ee" tileBg="#d1fadf" fg="#065f46"
                borderColor="#bbf0cf" metricColor="#166534"
                shadowColor="rgba(22,163,74,.18)"
                icon={<VerifiedUserRoundedIcon />} sub="Can sign in to portal"
                selected={activeSelected}
                onClick={() => toggleStatus("active")}
                ariaLabel="Filter parents: Active Login"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <SummaryCard
                label="No Login" value={summary.noLogin}
                tint="#f3edff" tileBg="#e4d8ff" fg="#5b21b6"
                borderColor="#ddd0fb" metricColor="#6d28d9"
                shadowColor="rgba(139,92,246,.18)"
                icon={<PersonOffRoundedIcon />} sub="No portal account"
                selected={noLoginSelected}
                onClick={() => toggleStatus("no_account")}
                ariaLabel="Filter parents: No Login"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <SummaryCard
                label="Linked Children" value={summary.linkedChildren}
                tint="#fff2e2" tileBg="#ffe1c2" fg="#9a3412"
                borderColor="#fdd6ac" metricColor="#b45309"
                shadowColor="rgba(234,88,12,.18)"
                icon={<FamilyRestroomRoundedIcon />} sub="Total parent–child links"
                selected={linkedSelected}
                onClick={toggleLinked}
                ariaLabel="Filter parents: With linked children"
              />
            </Grid>
          </Grid>
        );
      })()}

      {/* ── Filter toolbar ────────────────────────────────────── */}
      <Card
        elevation={0}
        sx={{
          p: { xs: 1.5, md: 2 },
          mb: 2.5,
          borderRadius: 2.5,
          border: "1px solid #dbe6f3",
          bgcolor: "#f2f6fb",
          boxShadow: "0 1px 4px rgba(15,23,42,.04)",
        }}
      >
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
              placeholder="Search by name, email, phone…"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CiSearch />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: "#ffffff",
                  "& fieldset": { borderColor: "#dbe6f3" },
                  "&:hover fieldset": { borderColor: "#93c5fd" },
                  "&.Mui-focused fieldset": { borderColor: "#3b82f6", boxShadow: "0 0 0 3px rgba(59,130,246,.15)" },
                },
              }}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2.5}>
            <Select
              fullWidth size="small" value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value as StatusFilter); setPage(0); }}
              sx={{
                borderRadius: 2,
                bgcolor: "#ffffff",
                "& fieldset": { borderColor: "#dbe6f3" },
                "&:hover fieldset": { borderColor: "#93c5fd" },
                "&.Mui-focused fieldset": { borderColor: "#3b82f6", boxShadow: "0 0 0 3px rgba(59,130,246,.15)" },
              }}
            >
              <MenuItem value="all">All statuses</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="disabled">Disabled</MenuItem>
              <MenuItem value="no_account">No Login</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={6} sm={3} md={2.5}>
            <Select
              fullWidth size="small" value={linkFilter}
              onChange={(e) => { setLinkFilter(e.target.value as LinkFilter); setPage(0); }}
              sx={{
                borderRadius: 2,
                bgcolor: "#ffffff",
                "& fieldset": { borderColor: "#dbe6f3" },
                "&:hover fieldset": { borderColor: "#93c5fd" },
                "&.Mui-focused fieldset": { borderColor: "#3b82f6", boxShadow: "0 0 0 3px rgba(59,130,246,.15)" },
              }}
            >
              <MenuItem value="all">All children</MenuItem>
              <MenuItem value="with">With linked children</MenuItem>
              <MenuItem value="without">Without linked children</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={12} md sx={{ display: "flex", justifyContent: { xs: "space-between", md: "flex-end" }, alignItems: "center", flexWrap: "wrap", gap: 1 }}>
            {hasActiveFilters && (
              <Button
                onClick={resetFilters}
                startIcon={<FilterAltOffRoundedIcon fontSize="small" />}
                aria-label="Clear all active filters"
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: 13,
                  color: "#475569",
                  bgcolor: "#e2e8f0",
                  borderRadius: 2,
                  height: 40,
                  px: 1.75,
                  "&:hover": { bgcolor: "#cbd5e1" },
                  "&:focus-visible": { outline: "2px solid #3b82f6", outlineOffset: 2 },
                }}
              >
                Clear Filters
              </Button>
            )}
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                px: 1.25,
                py: 0.5,
                borderRadius: 999,
                bgcolor: "#e0efff",
                color: "#1d4ed8",
                border: "1px solid #bfdbfe",
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: .2,
                height: 28,
              }}
            >
              {filtered.length} result{filtered.length !== 1 ? "s" : ""}
            </Box>
          </Grid>
        </Grid>
      </Card>

      {/* ── List body ─────────────────────────────────────────── */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 2.5,
          border: "1px solid #dbe6f3",
          borderTop: "3px solid #93c5fd",
          overflow: "hidden",
          bgcolor: "#ffffff",
          boxShadow: "0 4px 16px rgba(15,23,42,.05)",
        }}
      >
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <StepUpSpinner size="medium" />
          </Box>
        ) : isError ? (
          <Box textAlign="center" py={6} color="#dc2626" fontWeight={600}>
            Error fetching data
          </Box>
        ) : paged.length === 0 ? (
          <Box textAlign="center" py={6}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#334155" }}>No parents found</Typography>
            <Typography sx={{ fontSize: 13, color: "#64748b", mt: 0.5 }}>
              {hasActiveFilters ? "Try clearing filters or adjusting your search." : "Add a parent to get started."}
            </Typography>
            {hasActiveFilters && (
              <Button
                size="small"
                onClick={resetFilters}
                startIcon={<FilterAltOffRoundedIcon fontSize="small" />}
                sx={{ mt: 1.5, textTransform: "none", color: "#1d4ed8", fontWeight: 700 }}
              >
                Clear Filters
              </Button>
            )}
          </Box>
        ) : isMobile ? (
          <Box sx={{ p: 1.5 }}>
            {paged.map((row) => <MobileCard key={row.id} row={row} />)}
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "#eaf1f9" }}>
                  {["Parent", "Email", "Phone", "Username", "Linked Children", "Status", "Actions"].map((h, i) => (
                    <TableCell
                      key={h}
                      sx={{
                        fontSize: 11.5, fontWeight: 800, color: "#1e3a5f",
                        letterSpacing: .9, textTransform: "uppercase",
                        borderBottom: "1px solid #cddaea",
                        py: 1.75, px: 2,
                        textAlign: i === 6 ? "right" : "left",
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paged.map((row) => {
                  const accent = rowAccent(row.status);
                  return (
                  <TableRow
                    key={row.id}
                    sx={{
                      transition: "background-color .15s ease, box-shadow .15s ease",
                      position: "relative",
                      "& td:first-of-type": {
                        boxShadow: `inset 3px 0 0 0 ${accent.border}`,
                      },
                      "&:hover": {
                        bgcolor: accent.hover,
                        "& .MuiAvatar-root": { boxShadow: "0 3px 10px rgba(15,23,42,.12)", transform: "translateY(-1px)" },
                      },
                      "& td": { borderBottom: "1px solid #eef2f7", py: 1.75, px: 2, verticalAlign: "middle" },
                      "&:last-of-type td": { borderBottom: 0 },
                    }}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <ParentAvatar row={row} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontWeight: 700, fontSize: 14, color: "#0b1e3a",
                              transition: "color .15s ease",
                              "&:hover": { color: "#1d4ed8" },
                            }}
                            noWrap
                          >
                            {row.name || "—"}
                          </Typography>
                          <Typography sx={{ fontSize: 11.5, color: "#7c8aa3", fontWeight: 500 }}>
                            Parent Account
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {row.email
                        ? <Typography sx={{ fontSize: 13, color: "#0f172a" }}>{row.email}</Typography>
                        : <Muted />}
                    </TableCell>
                    <TableCell>
                      {row.phone
                        ? <Typography sx={{ fontSize: 13, color: "#0f172a" }}>{row.phone}</Typography>
                        : <Muted />}
                    </TableCell>
                    <TableCell>
                      {row.username
                        ? <Typography sx={{ fontSize: 12.5, color: "#0f172a", fontFamily: "monospace" }}>{row.username}</Typography>
                        : <Muted />}
                    </TableCell>
                    <TableCell>{renderChildren(row.children)}</TableCell>
                    <TableCell><StatusBadge status={row.status} /></TableCell>
                    <TableCell sx={{ textAlign: "right" }}>{renderActions(row)}</TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!isLoading && filtered.length > rowsPerPage && (
          <Box sx={{ p: 1.5, borderTop: "1px solid #eef2f7", display: "flex", justifyContent: "flex-end" }}>
            <Pagination
              count={filtered.length}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={(_: unknown, p: number) => setPage(p)}
            />
          </Box>
        )}
      </Card>

      {toDelete && (
        <DeletePopup
          open={openDelete}
          handleCloseDelete={() => {
            setOpenDelete(false);
            setToDelete(null);
          }}
          onConfirm={handleDelete}
          title="Delete Parent"
          content={`Are you sure you want to delete "${toDelete.name}"?`}
        />
      )}

      <Dialog open={Boolean(toResolve)} onClose={closeResolve} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {toResolve?.name} is linked to {toResolve?.children_count} child
          {toResolve && toResolve.children_count !== 1 ? "ren" : ""}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "text.secondary", fontSize: 13, mb: 1.5 }}>
            Choose what to do with this parent:
          </Typography>
          <RadioGroup
            value={resolveMode}
            onChange={(e) => setResolveMode(e.target.value as ResolveMode)}
          >
            <FormControlLabel
              value="disable"
              disabled={!toResolve?.has_account}
              control={<Radio />}
              label={
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                    Disable portal login only
                  </Typography>
                  <Typography sx={{ color: "text.secondary", fontSize: 12.5 }}>
                    {toResolve?.has_account
                      ? "Keeps the parent and all child links; only blocks portal sign-in."
                      : "This parent has no portal account."}
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="detach"
              control={<Radio />}
              label={
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                    Remove from all children
                  </Typography>
                  <Typography sx={{ color: "text.secondary", fontSize: 12.5 }}>
                    Unlinks the parent from every child but keeps the parent record.
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="delete"
              control={<Radio />}
              label={
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: 14, color: "#b91c1c" }}>
                    Delete parent and all links
                  </Typography>
                  <Typography sx={{ color: "text.secondary", fontSize: 12.5 }}>
                    Removes the parent from every child and deletes the parent record.
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeResolve} color="inherit">Cancel</Button>
          <Button
            variant="contained"
            color={resolveMode === "delete" ? "error" : "primary"}
            onClick={handleResolve}
          >
            {resolveMode === "disable"
              ? "Disable Login"
              : resolveMode === "detach"
              ? "Remove Links"
              : "Delete Parent"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ParentListPage;
